import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { fetchCarpoolPosts, createCarpoolPost, deleteCarpoolPost } from "../api/carpool";
import { fetchRequests, createRequest, updateRequest } from "../api/requests";
import { fetchBuddies, removeBuddy } from "../api/buddies";
import { searchUsers } from "../api/users";
import { fetchProfile } from "../api/user";
import { RESORTS, RESORTS_BY_ID } from "../data/resorts";
import { ABILITY_LEVELS } from "../constants/abilityLevels";
import { HOME_LOCATION_LABELS } from "../constants/homeLocations";

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function LevelFilterRow({ level, onChange }) {
  return (
    <div className="pill-row">
      <button className={`pill${level === null ? " on" : ""}`} onClick={() => onChange(null)}>
        All levels
      </button>
      {ABILITY_LEVELS.map((l) => (
        <button key={l} className={`pill${level === l ? " on" : ""}`} onClick={() => onChange(l)}>
          {l}
        </button>
      ))}
    </div>
  );
}

function PostRideForm({ onDone, defaultOrigin }) {
  const queryClient = useQueryClient();
  const [resortId, setResortId] = useState(RESORTS[0].resortId);
  const [origin, setOrigin] = useState(defaultOrigin ?? "");
  const [departureAt, setDepartureAt] = useState("");
  const [seatsTotal, setSeatsTotal] = useState(3);
  const [notes, setNotes] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createCarpoolPost({
        route: RESORTS_BY_ID[resortId].route,
        resortId,
        origin: origin.trim(),
        departureAt: departureAt ? new Date(departureAt).toISOString() : "",
        seatsTotal: Number(seatsTotal),
        notes: notes.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carpoolPosts"] });
      onDone?.();
    },
  });

  return (
    <form
      className="glass carpool-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (origin.trim() && departureAt) mutation.mutate();
      }}
    >
      <select value={resortId} onChange={(e) => setResortId(e.target.value)}>
        {RESORTS.map((r) => (
          <option key={r.resortId} value={r.resortId}>
            {r.name} · {r.route.toUpperCase()}
          </option>
        ))}
      </select>
      <input
        placeholder="Leaving from (e.g. Berkeley)"
        value={origin}
        onChange={(e) => setOrigin(e.target.value)}
        required
      />
      <input
        type="datetime-local"
        value={departureAt}
        onChange={(e) => setDepartureAt(e.target.value)}
        required
      />
      <input
        type="number"
        min="1"
        max="8"
        value={seatsTotal}
        onChange={(e) => setSeatsTotal(e.target.value)}
        required
      />
      <input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button type="submit" className="btn-accent" disabled={mutation.isPending}>
        {mutation.isPending ? "Posting…" : "Post ride"}
      </button>
      {mutation.isError && <p className="badge-warn">Couldn't post — try again.</p>}
    </form>
  );
}

function CarpoolBoard({ level, userId, requests }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["carpoolPosts", level],
    queryFn: () => fetchCarpoolPosts(level),
  });

  const requestSeat = useMutation({
    mutationFn: (post) => createRequest({ toUserId: post.userId, requestType: "carpool_seat", postId: post.postId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["requests"] }),
  });

  const deletePost = useMutation({
    mutationFn: (postId) => deleteCarpoolPost(postId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["carpoolPosts"] }),
  });

  const outgoingSeatRequest = (postId) =>
    requests?.outgoing?.find((r) => r.requestType === "carpool_seat" && r.postId === postId);

  if (isLoading) return <p>Loading rides…</p>;
  if (error) return <p className="badge-warn">Couldn't load the carpool board.</p>;
  if (!data?.posts?.length) return <p className="sec-dek">No rides posted yet — be the first.</p>;

  return (
    <ul className="plain">
      {data.posts.map((post) => {
        const isMine = post.userId === userId;
        const seatsLeft = post.seatsTotal - post.seatsFilled;
        const existing = outgoingSeatRequest(post.postId);
        return (
          <li key={post.postId} className="glass post-row">
            <div className="bav">{(post.posterName || "?").charAt(0).toUpperCase()}</div>
            <div className="post-main">
              <div className="post-name">{post.posterName}</div>
              <div className="post-route">
                {RESORTS_BY_ID[post.resortId]?.name ?? post.resortId} from {post.origin} ·{" "}
                {post.route.toUpperCase()}
              </div>
              <div className="post-when mono">{formatWhen(post.departureAt)}</div>
              {post.notes && <div className="sec-dek">{post.notes}</div>}
            </div>
            <div className="post-actions">
              {post.posterAbilityLevel && <span className="lvl-pill">{post.posterAbilityLevel}</span>}
              {isMine ? (
                <button className="pill" onClick={() => deletePost.mutate(post.postId)} disabled={deletePost.isPending}>
                  Delete
                </button>
              ) : seatsLeft <= 0 ? (
                <span className="seats-left">Full</span>
              ) : existing ? (
                <span className="seats-left">
                  {existing.status === "accepted" ? "You're in" : existing.status === "declined" ? "Declined" : "Requested"}
                </span>
              ) : (
                <button
                  className="req-btn"
                  onClick={() => requestSeat.mutate(post)}
                  disabled={requestSeat.isPending}
                >
                  Request seat
                </button>
              )}
              <span className="seats-left">{seatsLeft} seat{seatsLeft === 1 ? "" : "s"} left</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BuddySearch({ level, requests, buddyIds }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["userSearch", level],
    queryFn: () => searchUsers(level),
  });

  const addBuddy = useMutation({
    mutationFn: (toUserId) => createRequest({ toUserId, requestType: "buddy_match" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["requests"] }),
  });

  const outgoingBuddyRequest = (toUserId) =>
    requests?.outgoing?.find((r) => r.requestType === "buddy_match" && r.toUserId === toUserId);

  if (isLoading) return <p>Loading riders…</p>;
  if (error) return <p className="badge-warn">Couldn't load the buddy list.</p>;
  if (!data?.users?.length) return <p className="sec-dek">No other riders match this level yet.</p>;

  return (
    <ul className="plain">
      {data.users.map((u) => {
        if (buddyIds.has(u.userId)) return null;
        const existing = outgoingBuddyRequest(u.userId);
        return (
          <li key={u.userId} className="glass post-row">
            <div className="bav">{(u.name || "?").charAt(0).toUpperCase()}</div>
            <div className="post-main">
              <div className="post-name">{u.name}</div>
            </div>
            <div className="post-actions">
              {u.abilityLevel && <span className="lvl-pill">{u.abilityLevel}</span>}
              {existing ? (
                <span className="seats-left">
                  {existing.status === "accepted" ? "Buddies" : existing.status === "declined" ? "Declined" : "Requested"}
                </span>
              ) : (
                <button className="req-btn" onClick={() => addBuddy.mutate(u.userId)} disabled={addBuddy.isPending}>
                  Add buddy
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function MyRequests({ requests }) {
  const queryClient = useQueryClient();
  const respond = useMutation({
    mutationFn: ({ requestId, action }) => updateRequest(requestId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["carpoolPosts"] });
      queryClient.invalidateQueries({ queryKey: ["buddies"] });
    },
  });

  const incoming = requests?.incoming?.filter((r) => r.status === "pending") ?? [];
  if (incoming.length === 0) return <p className="sec-dek">No pending requests.</p>;

  return (
    <ul className="plain">
      {incoming.map((r) => (
        <li key={r.requestId} className="glass post-row">
          <div className="bav">{(r.fromName || "?").charAt(0).toUpperCase()}</div>
          <div className="post-main">
            <div className="post-name">{r.fromName}</div>
            <div className="post-route">
              {r.requestType === "carpool_seat" ? `Wants a seat · ${r.routeSummary ?? ""}` : "Wants to be your buddy"}
            </div>
          </div>
          <div className="post-actions">
            <button className="req-btn" onClick={() => respond.mutate({ requestId: r.requestId, action: "accept" })}>
              Accept
            </button>
            <button className="pill" onClick={() => respond.mutate({ requestId: r.requestId, action: "decline" })}>
              Decline
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function MyBuddies() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["buddies"], queryFn: fetchBuddies });
  const remove = useMutation({
    mutationFn: (buddyUserId) => removeBuddy(buddyUserId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["buddies"] }),
  });

  if (!data?.buddies?.length) return <p className="sec-dek">No buddies yet — add one from Find a buddy below.</p>;

  return (
    <ul className="plain">
      {data.buddies.map((b) => (
        <li key={b.buddyUserId} className="glass post-row">
          <div className="bav">{(b.name || "?").charAt(0).toUpperCase()}</div>
          <div className="post-main">
            <div className="post-name">{b.name}</div>
          </div>
          <div className="post-actions">
            {b.abilityLevel && <span className="lvl-pill">{b.abilityLevel}</span>}
            <button className="pill" onClick={() => remove.mutate(b.buddyUserId)} disabled={remove.isPending}>
              Remove
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

// Phase 3 — carpool board + ability-matched buddy search. Shipped visible in the nav (not behind a
// feature flag, per explicit direction) — see src/App.jsx.
export function CarpoolTab() {
  const { user } = useAuth();
  const [level, setLevel] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.userId],
    queryFn: () => fetchProfile(user.userId),
    enabled: !!user?.userId,
  });
  const { data: requests } = useQuery({ queryKey: ["requests"], queryFn: fetchRequests });
  const { data: buddiesData } = useQuery({ queryKey: ["buddies"], queryFn: fetchBuddies });
  const buddyIds = new Set((buddiesData?.buddies ?? []).map((b) => b.buddyUserId));

  return (
    <section>
      <div className="hd-row">
        <div>
          <h2>Carpool &amp; buddies</h2>
          <p className="sec-dek">Post a ride, request a seat, or find a buddy at your level.</p>
        </div>
        <button className="post-btn btn-accent" onClick={() => setShowForm((s) => !s)}>
          {showForm ? "Cancel" : "+ Post"}
        </button>
      </div>

      {showForm && (
        <PostRideForm
          onDone={() => setShowForm(false)}
          defaultOrigin={profile?.homeLocation ? HOME_LOCATION_LABELS[profile.homeLocation] : ""}
        />
      )}

      <LevelFilterRow level={level} onChange={setLevel} />

      <div className="sec-hd">
        <span className="sec-title">Carpool board</span>
      </div>
      <CarpoolBoard level={level} userId={user?.userId} requests={requests} />

      <div className="sec-hd">
        <span className="sec-title">My requests</span>
      </div>
      <MyRequests requests={requests} />

      <div className="sec-hd">
        <span className="sec-title">My buddies</span>
      </div>
      <MyBuddies />

      <div className="sec-hd">
        <span className="sec-title">Find a buddy</span>
      </div>
      <BuddySearch level={level} requests={requests} buddyIds={buddyIds} />
    </section>
  );
}
